<!--
  The notification owner (refresh_plan.md 3.1.7): "One store module, one
  <LwToastRegion aria-live="polite"> mounted in the shell." This is that
  region. It has no state of its own; it is a read-only view over
  ../../store/toasts.js, which owns the queue and every auto-dismiss timer.

  Announcement model chosen: two live regions, both mounted unconditionally
  (never behind a v-if), because a live region created at the moment a
  message arrives is often missed entirely by a screen reader - only a
  region that already existed on the page reliably picks up children being
  added to it later. That is also why the outer wrapper and both inner
  regions render regardless of queue length.

    - "polite" (role="status", aria-live="polite", aria-atomic="false")
      carries every tone except danger.
    - "assertive" (aria-live="assertive", aria-atomic="false") carries only
      danger toasts, and interrupts.

  The assertive region deliberately does NOT set role="alert" on itself.
  LwNotice already renders role="alert" on its own root when tone is
  "danger" (see LwNotice.vue's `role` computed), and every toast routed into
  this bucket is, by construction, a danger toast. Adding role="alert" to
  the wrapping region as well would nest two role="alert" elements around
  one message; assistive tech that treats each role="alert" node as an
  independent interruption would then announce the same toast twice. One
  role="alert", supplied exactly once by LwNotice, is enough. The wrapper
  supplies only aria-live="assertive", which is what satisfies the
  "region must already exist" requirement for that channel.
-->
<template>
  <div class="lw-c-toast-region" :style="{ pointerEvents: 'none' }">
    <transition-group
      tag="div"
      name="lw-c-toast-region__fade"
      role="status"
      aria-live="polite"
      aria-atomic="false"
      class="lw-c-toast-region__group"
    >
      <div
        v-for="toast in politeToasts"
        :key="toast.id"
        class="lw-c-toast-region__item"
        :style="{ pointerEvents: 'auto' }"
      >
        <LwNotice :tone="toast.tone" dismissible @dismiss="dismiss(toast.id)">
          {{ toast.message }}
        </LwNotice>
      </div>
    </transition-group>

    <transition-group
      tag="div"
      name="lw-c-toast-region__fade"
      aria-live="assertive"
      aria-atomic="false"
      class="lw-c-toast-region__group"
    >
      <div
        v-for="toast in assertiveToasts"
        :key="toast.id"
        class="lw-c-toast-region__item"
        :style="{ pointerEvents: 'auto' }"
      >
        <LwNotice :tone="toast.tone" dismissible @dismiss="dismiss(toast.id)">
          {{ toast.message }}
        </LwNotice>
      </div>
    </transition-group>
  </div>
</template>

<script>
import LwNotice from './LwNotice.vue';

export default {
  name: 'LwToastRegion',
  components: { LwNotice },
  computed: {
    // Defensive per the house idiom (app.vue's showBanner, Devices.vue:352-355:
    // "$store && $store.getters && ..."): the shell's own tests mock $store as
    // a bare object, and a hard `this.$store.getters['toasts/items']` throws
    // against that double.
    toasts() {
      return (this.$store && this.$store.getters && this.$store.getters['toasts/items']) || [];
    },
    // Partitioned straight off each toast's own `tone`, not off the store's
    // `toasts/hasAssertive` getter: that getter only answers "does one exist
    // somewhere", and rendering needs to know which ones, per item, on every
    // render. Reading it here would just be a second, less useful look at the
    // same field this filter already reads.
    politeToasts() {
      return this.toasts.filter(toast => toast.tone !== 'danger');
    },
    assertiveToasts() {
      return this.toasts.filter(toast => toast.tone === 'danger');
    },
  },
  methods: {
    dismiss(id) {
      // Same defensive idiom, extended to the action: notify()/dismiss() are
      // the queue's only mutation path (the region must never commit to the
      // store directly), and the bare-object store double used by the shell's
      // tests has no `dispatch` at all.
      if (this.$store && typeof this.$store.dispatch === 'function') {
        this.$store.dispatch('toasts/dismiss', id);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../sass/mixins.generated';

// Fixed, off the page's flow, above every surface but a dialog (the token
// file's z-index order puts toast highest of all, because a toast has to be
// able to report its own failure over an open dialog). Pointer-events is
// bound inline above rather than declared here: that is what makes the
// "an empty region must not swallow a click" contract assertable at all,
// since jsdom does not compute this scoped stylesheet (the same gap every
// other test in this library works around), and inline style is also the
// mechanism that actually wins in a real browser regardless.
.lw-c-toast-region {
  position: fixed;
  top: var(--lw-space-4);
  right: var(--lw-space-4);
  z-index: var(--lw-z-toast);
  display: flex;
  flex-direction: column;
  gap: var(--lw-space-3);
  // No token names a toast column's width; the layout tokens are page rail
  // and prose measure, neither of which is this. 360px is picked to hold a
  // full sentence without spanning a wide monitor - raw geometry, no token
  // to reach for.
  width: min(100%, 360px);
  max-height: 100vh;
  overflow-y: auto;
}

.lw-c-toast-region__group {
  display: flex;
  flex-direction: column;
  gap: var(--lw-space-3);
}

.lw-c-toast-region__item {
  border-radius: var(--lw-radius-sm);
  // Elev-3, the popover/menu level: a toast floats above the page the way a
  // menu does, which is why it gets a shadow that LwNotice's own elev-0
  // border never carries when the same component sits flat on a page.
  box-shadow: var(--lw-elev-3-shadow);
}

.lw-c-toast-region__fade-enter-active,
.lw-c-toast-region__fade-leave-active {
  transition:
    opacity var(--lw-motion-base) var(--lw-motion-ease),
    transform var(--lw-motion-base) var(--lw-motion-ease);
}

.lw-c-toast-region__fade-enter-from,
.lw-c-toast-region__fade-leave-to {
  opacity: 0;
  // Raw geometry, no token: a small settle distance for the enter/leave
  // slide, not a spacing value placed between two elements.
  transform: translateY(-6px);
}
</style>
